import * as React from 'react';

import { NavigationGuardProvider } from 'next-navigation-guard';
import { SnackbarProvider } from 'notistack';

import CssBaseline from '@mui/material/CssBaseline';

import {
  ApolloClientProvider,
  AuthProvider,
  BreadcrumbsProvider,
  I18nProvider,
  LocalizationProvider,
  NotificationCountProvider,
  QueryProvider,
  SocketProvider,
  ThemeProvider,
  UserProvider,
} from '@itp/talent';
import { CVParseProvider } from '@itp/talent/context/cvParseContext';
import { ImpersonateProvider } from '@itp/talent/context/impersonateContext';
import { LocalAuthProvider } from '@itp/talent/context/localAuthContext';
import { NotificationsProvider } from '@itp/talent/context/notificationsContext';
import { ProfileProvider } from '@itp/talent/pages/talentProfilePage/profileContext';

import { DropClaimHandler } from '../../dropClaim/dropClaimHandler';
import { RouteProtection } from '../../routeProtection';
import { VersionCheck } from '../../versionCheck';
import { SharedHeaders } from '../components/sharedHeaders';

type AppSkeletonProps = {
  children?: React.ReactNode;
};

export const AppSkeleton: React.FC<AppSkeletonProps> = ({ children }) => {
  const providers = [
    LocalAuthProvider,
    AuthProvider,
    I18nProvider,
    RouteProtection,
    ImpersonateProvider,
    ApolloClientProvider,
    LocalizationProvider,
    ThemeProvider,
    UserProvider,
    QueryProvider,
    NotificationsProvider,
    SnackbarProvider,
    SocketProvider,
    NotificationCountProvider,
    NavigationGuardProvider,
    BreadcrumbsProvider,
    ProfileProvider,
    CVParseProvider,
  ];

  const innerContent = (
    <>
      <SharedHeaders />
      <CssBaseline />
      <VersionCheck />
      <DropClaimHandler />
      {children}
    </>
  );

  return providers.reduceRight((acc, Provider) => {
    return <Provider>{acc}</Provider>;
  }, innerContent);
};
